import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Bell, Check, Info, CheckCircle2, AlertTriangle, X } from "lucide-react";

const SEV_ICON = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: AlertTriangle };
const SEV_COLOR = { info: "#8FCAEF", success: "#059669", warning: "#F2C98A", danger: "#E88A93" };

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const { lang } = useI18n();

  const load = async () => {
    try {
      const [u, list] = await Promise.all([
        api.get("/notifications/unread_count"),
        api.get("/notifications"),
      ]);
      setUnread(u.data.unread || 0);
      setItems(list.data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark_all_read");
      await load();
      toast.success(lang === "fr" ? "Toutes marquées comme lues" : "All marked as read");
    } catch {}
  };

  return (
    <>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center justify-center h-8 w-8 rounded-md transition"
        style={{ border: "1px solid #C5A88033", color: "#FBF6EACC" }}
      >
        <Bell className="h-4 w-4" strokeWidth={1.8} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#0B2545] text-white font-mono text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="notifications-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md h-full bg-[#F8F7F3] border-l border-[#E4E1D8] flex flex-col">
            <div className="px-5 py-4 border-b border-[#E4E1D8] flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Centre de notifications" : "Notifications Centre"}
                </div>
                <div className="mt-0.5 font-display text-lg text-[#1F2937] tracking-tight">
                  {unread > 0
                    ? (lang === "fr" ? `${unread} non lues` : `${unread} unread`)
                    : (lang === "fr" ? "Tout à jour" : "All caught up")}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={markAll} data-testid="notifications-mark-all"
                        className="p-2 text-xs text-[#5B5B62] hover:text-[#1F2937]" title={lang === "fr" ? "Tout lu" : "Mark all"}>
                  <Check className="h-4 w-4" strokeWidth={1.8} />
                </button>
                <button onClick={() => setOpen(false)}
                        className="p-2 text-[#5B5B62] hover:text-[#1F2937]">
                  <X className="h-4 w-4" strokeWidth={1.8} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-8 text-center text-xs text-[#8B8B8F] font-mono">
                  {lang === "fr" ? "Aucune notification." : "No notifications."}
                </div>
              ) : (
                <ul className="divide-y divide-[#E4E1D8]">
                  {items.map((n) => {
                    const Ico = SEV_ICON[n.severity] || Info;
                    return (
                      <li key={n.notification_id} className={`px-5 py-4 ${!n.read ? "bg-white" : ""}`}>
                        <div className="flex items-start gap-3">
                          <Ico className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.8} style={{ color: SEV_COLOR[n.severity] || "#5B5B62" }} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <div className="text-sm text-[#1F2937]">{n.title}</div>
                              {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-[#0B2545] flex-shrink-0" />}
                            </div>
                            {n.body && <div className="mt-1 text-xs text-[#5B5B62] leading-relaxed">{n.body}</div>}
                            <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-[#8B8B8F]">
                              {new Date(n.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
